/**
 * Public landing marketing prose (issue #17), keyed by tenant slug.
 *
 * Branding (name / logo / parent_org) stays DB-driven via `GET /tenant`; only the
 * curated public content lives here. Program acquisition links stay here as
 * tenant data rather than being embedded in the shared gateway component.
 */
interface LandingBase {
  /**
   * Canonical ownership boundary for public admissions. Portal admissions use
   * the shared LMS application form; external admissions leave this site.
   */
  admissions: AdmissionsContract;
  authGateway: AuthGatewayContent;
  footer: {
    description: string;
    /**
     * Where a person writes to the school about their own records. Lives on the
     * base rather than on `AcademyLandingContent` because the legal documents
     * need it from EVERY tenant: a program tenant that runs admissions off-site
     * still holds student records here and still has to name a contact. Mufid
     * had none, so its published privacy policy told a reader nowhere to write.
     */
    email: string | null;
  };
  /**
   * The parent that is the responsible legal entity for this school, or null
   * when the school is its own entity (decision D1).
   *
   * Deliberately NOT derived from the `parent_org` branding field: that field is
   * presentational, an administrator can edit it, and an edit to it must never
   * silently change which entity a published legal document names as the party.
   */
  operatorEntityName: string | null;
}

export interface AuthGatewayContent {
  /** Compatibility projection for AuthGateway links; derived from `admissions`. */
  admissionsUrl: string;
  currentAudience: string;
  currentDescription: string;
  prospectiveAudience: string;
  prospectiveDescription: string;
  supportUrl: string;
}

export interface PortalApplicationContent {
  eyebrow: string;
  heading: string;
  lede: string;
  notePlaceholder: string;
  subtitle: string;
  successLead: string;
}

export type AdmissionsContract =
  | {
      kind: "portal";
      application: PortalApplicationContent;
      url: "/apply";
    }
  | {
      kind: "external";
      url: `https://${string}`;
    };

export interface AcademyLandingContent extends LandingBase {
  kind: "academy";
  /** One-line byline under the sign-in form (affiliation / locations / modality). */
  portalFooter: string;
  footer: {
    description: string;
    audience: string;
    email: string | null;
    locations: string;
  };
  academy: {
    affiliation: string;
    hero: {
      h1: string;
      lines: string[];
      sub: string;
      mission: string;
      facts: { label: string; value: string }[];
    };
    about: {
      eyebrow: string;
      h2: string;
      paragraphs: string[];
    };
    approach: {
      eyebrow: string;
      h2: string;
      lede: string;
      items: { title: string; lines: string[]; body: string }[];
    };
    admissions: {
      eyebrow: string;
      h2: string;
      lede: string;
      steps: { title: string; body: string }[];
    };
    application: {
      eyebrow: string;
      h2: string;
      lede: string;
      /** Shown under the hero action on a phone and beside the facts on a desktop. */
      reassurance: string;
    };
    decisionFacts: { label: string; value: string; mobileValue?: string }[];
  };
}

export interface ProgramLandingContent extends LandingBase {
  kind: "program";
}

export type LandingContent = AcademyLandingContent | ProgramLandingContent;

const murtadaAdmissions = {
  kind: "portal",
  url: "/apply",
  application: {
    eyebrow: "Admissions",
    heading: "Apply for the 2026-27 academic year.",
    lede: "A director reviews every application and follows up within a few days.",
    notePlaceholder:
      "Student age, school year, prior study, scheduling, or questions…",
    subtitle: "2026-27 academic year admissions · no account, no payment",
    successLead:
      "A director will review your application and reach out within a few days, usually by email, with placement and schedule details.",
  },
} as const satisfies AdmissionsContract;

export const murtadaLandingContent: AcademyLandingContent = {
  kind: "academy",
  operatorEntityName: "IMAM",
  admissions: murtadaAdmissions,
  authGateway: {
    admissionsUrl: murtadaAdmissions.url,
    currentAudience: "Current students & instructors",
    currentDescription: "Sign in to access your academy portal",
    prospectiveAudience: "Prospective students",
    prospectiveDescription: "Apply to Murtada",
    supportUrl: "mailto:info@murtadaacademy.org",
  },
  academy: {
    affiliation: "An Educational Program by IMAM",
    hero: {
      h1: "Two evenings a week. A foundation for life.",
      lines: ["Two evenings", "a week.", "A foundation", "for life."],
      sub: "Creative, engaging, practical Islamic study.",
      mission:
        "Helping students understand, articulate, and thoughtfully defend their Islamic beliefs through creative, engaging, practical study.",
      facts: [
        { label: "Ages", value: "14-21" },
        { label: "Weekly rhythm", value: "Mondays + Wednesdays" },
        { label: "Session time", value: "6:00-8:00 PM" },
        { label: "Admissions", value: "2026-27 academic year applications" },
      ],
    },
    about: {
      eyebrow: "About Murtada Academy",
      h2: "A place to understand your faith and grow into it.",
      paragraphs: [
        "Murtada Academy is an in-person Islamic formation program for young Muslims ages 14-21. It is designed for students ready to build a serious religious foundation through sustained study, thoughtful discussion, and practical application.",
        "What students learn should shape how they explain their beliefs, make choices, and live.",
      ],
    },
    approach: {
      eyebrow: "Our approach",
      h2: "We form minds, build conviction, and shape character.",
      lede: "The academy turns knowledge into confidence and faith into action through three connected commitments.",
      items: [
        {
          title: "Understand your faith.",
          lines: ["Understand", "your faith."],
          body: "Build a solid religious foundation by understanding Islamic teachings with purpose and clarity.",
        },
        {
          title: "Articulate it.",
          lines: ["Articulate it."],
          body: "Ask better questions and practice explaining and thoughtfully defending what you believe.",
        },
        {
          title: "Live it with conviction.",
          lines: ["Live it with", "conviction."],
          body: "Connect what you learn to character, choices, community, and everyday life.",
        },
      ],
    },
    admissions: {
      eyebrow: "Admissions overview",
      h2: "A clear path to join.",
      lede: "The application starts a conversation about the student, the academy's expectations, and the right placement.",
      steps: [
        {
          title: "Apply",
          body: "Submit the student's basic information and the best way to reach you.",
        },
        {
          title: "Interview",
          body: "A director follows up, usually by email, to arrange a conversation about fit and expectations.",
        },
        {
          title: "Placement",
          body: "We confirm fit, placement, and the next enrollment steps.",
        },
      ],
    },
    application: {
      eyebrow: "2026-27 academic year admissions",
      h2: "Apply for the 2026-27 academic year.",
      lede: "A director reviews every application and follows up within a few days.",
      reassurance: "A director follows up within a few days.",
    },
    decisionFacts: [
      { label: "Weekly rhythm", value: "Mondays + Wednesdays" },
      { label: "Session time", value: "6:00-8:00 PM" },
      { label: "Age range", value: "14-21" },
      {
        label: "Format",
        value: "In-person instruction in English",
        mobileValue: "In person, in English",
      },
      { label: "Start date", value: "September 14, 2026" },
      { label: "Tuition", value: "$599" },
    ],
  },
  portalFooter:
    "An Educational Program by IMAM · Ages 14-21 · Monday and Wednesday evenings",
  footer: {
    description: "Creative, engaging, and practical Islamic learning.",
    audience: "For admitted students and faculty",
    email: "info@murtadaacademy.org",
    locations: "Mondays + Wednesdays · 6:00-8:00 PM",
  },
};

const mufidAdmissions = {
  kind: "portal",
  url: "/apply",
  application: {
    eyebrow: "Admissions",
    heading: "Apply to Mufid Seminary.",
    lede:
      "Share your information with the Mufid admissions team for review and follow-up.",
    notePlaceholder:
      "Prior study, academic goals, scheduling, or questions…",
    subtitle: "Seminary admissions · no account, no payment",
    successLead:
      "The Mufid admissions team will review your application and follow up by email with next steps.",
  },
} as const satisfies AdmissionsContract;

const mufid: ProgramLandingContent = {
  kind: "program",
  operatorEntityName: null,
  admissions: mufidAdmissions,
  authGateway: {
    admissionsUrl: mufidAdmissions.url,
    currentAudience: "Current students & faculty",
    currentDescription: "Sign in to access your portal",
    prospectiveAudience: "Prospective students",
    prospectiveDescription: "Apply to Mufid",
    supportUrl: "https://www.mufidseminary.org/contact-us",
  },
  footer: {
    description: "Classical seminary formation in a live cohort.",
    email: "info@mufidseminary.org",
  },
};

const CONTENT: Record<"murtada" | "mufid", LandingContent> = {
  murtada: murtadaLandingContent,
  mufid,
};

/** Marketing prose for a tenant slug; unknown slugs fall back to Murtada. */
export function getLandingContent(slug: string): LandingContent {
  return CONTENT[slug as "murtada" | "mufid"] ?? murtadaLandingContent;
}
